import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import { SearchHeader } from "../QrCodeManagement/components/SearchHeader";
import { RfidFormDialog } from "./components/RfidFormDialog";
import { RfidCard, useRfidCards } from "./hooks/useRfidCards";
import { RfidActionsCell } from "./components/RfidActionsCell";
import { AssignRfidDialog } from "./components/AssignRfidDialog";
import { ConfirmationDialog } from "./components/ConfirmationDialog";
import { Tag } from "primereact/tag";
import { formatExpiry, isExpired } from "../../utils/rfidExpiry";

type ModalState =
  | { name: null, uid?: never, cardCode?: never }
  | { name: "assign"; uid: string }
  | { name: "unassign"; uid: string }
  | { name: "deactivate"; uid: string }
  | { name: "delete"; uid: string }
  | { name: "update"; cardCode: string; uid: string; validUntil: string | null; status: RfidCard["status"]; }
  | { name: "create", uid?: never };

const RfidManagement: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { rfidCards, initialLoading, refreshing, createRfidCard, assignOwnerToCard, deactivateCard, unassignOwnerFromCard, deleteCard, updateCard } = useRfidCards(toast);

  const [search, setSearch] = useState<string>("");
  const [modal, setModal] = useState<ModalState>({ name: null });

  const filters = useMemo(
    () => ({
      global: { value: search, matchMode: FilterMatchMode.CONTAINS },
    }),
    [search]
  );

  const header = useMemo(() => (
    <SearchHeader
      value={search}
      onChange={setSearch}
      placeholder="Search ..."
      helperText="Search by uid, card code or owner emaid"
    />
  ), [search]);

  const onAssign = useCallback((uid: string) => setModal({ name: "assign", uid }), []);
  const onUnassign = useCallback((uid: string) => setModal({ name: "unassign", uid }), []);
  const onDeactivate = useCallback((uid: string) => setModal({ name: "deactivate", uid }), []);
  const onDelete = useCallback((uid: string) => setModal({ name: "delete", uid }), []);
  const onUpdate = useCallback((uid: string, cardCode: string, validUntil: string | null, status: RfidCard["status"]) => setModal({ name: "update", uid, cardCode, validUntil, status }), []);
  const onCreate = useCallback(() => setModal({ name: "create" }), []);
  const closeModal = useCallback(() => setModal({ name: null }), []);

  const renderModal = () => {
    switch (modal.name) {
      case "create":
        return (
          <RfidFormDialog
            mode="create"
            visible
            onHide={closeModal}
            onSubmit={async (uid, cardCode, validUntil) => {
              if (uid && cardCode) {
                try {
                  await createRfidCard(uid, cardCode, validUntil);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        );
      case "assign":
        return (
          <AssignRfidDialog uid={modal.uid} visible onHide={closeModal}
            onAssign={async (uid, emaid) => {
              if (uid && emaid) {
                try {
                  await assignOwnerToCard(uid, emaid);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        )
      case "unassign":
        return (
          <ConfirmationDialog
            uid={modal.uid}
            visible
            onHide={closeModal}
            header="Unassign RFID Card"
            message={`Are you sure you want to unassign RFID Card with uid ${modal.uid}?`}
            confirmLabel="Unassign"
            confirmIcon="pi pi-user-minus"
            onConfirm={async () => {
              if (modal.uid) {
                try {
                  await unassignOwnerFromCard(modal.uid);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        )
      case "deactivate":
        return (
          <ConfirmationDialog
            uid={modal.uid}
            visible
            onHide={closeModal}
            header="Deactivate RFID Card"
            message={`Are you sure you want to deactivate RFID Card with uid ${modal.uid}?`}
            confirmLabel="Deactivate"
            confirmIcon="pi pi-user-minus"
            onConfirm={async () => {
              if (modal.uid) {
                try {
                  await deactivateCard(modal.uid);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        )
      case "delete":
        return (
          <ConfirmationDialog
            uid={modal.uid}
            visible
            onHide={closeModal}
            header="Delete RFID Card"
            message={`Are you sure you want to delete RFID Card with uid ${modal.uid}? This action cannot be undone.`}
            confirmLabel="Delete"
            confirmIcon="pi pi-trash"
            isDelete
            onConfirm={async () => {
              if (modal.uid) {
                try {
                  await deleteCard(modal.uid);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        )

      case "update": {
        // strictNullChecks is off (see tsconfig), so the switch does not narrow
        // out the `{ name: null }` member; assert the concrete "update" shape.
        const m = modal as { name: "update"; uid: string; cardCode: string; validUntil: string | null; status: RfidCard["status"] };
        return (
          <RfidFormDialog
            mode="update"
            initialUid={m.uid}
            initialCardCode={m.cardCode}
            initialValidUntil={m.validUntil}
            // Assigned (ACTIVE) cards may only have their expiry changed; the card
            // code stays locked (matches the backend rule for assigned cards).
            lockCardCode={m.status === "ACTIVE"}
            visible
            onHide={closeModal}
            onSubmit={async (uid, cardCode, validUntil) => {
              if (uid && cardCode) {
                try {
                  await updateCard(uid, cardCode, validUntil);
                  closeModal();
                } catch {
                  // keep modal open on error
                }
              }
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const statusBody = (rowData: RfidCard) => {
    let severity: "success" | "warning" | "danger" | null = null;
    switch (rowData.status) {
      case "ACTIVE":
        severity = "success";
        break;
      case "INACTIVE":
        severity = "warning";
        break;
      case "DEACTIVATED":
        severity = "danger";
        break;
      default:
        severity = null;
    }
    return <Tag value={rowData.status} severity={severity} style={{ color: "#fff", ...(rowData.status === "DEACTIVATED" ? { borderColor: "#d4d4d4" } : {}) }} />;
  }

  const validUntilBody = (rowData: RfidCard) => (
    <div className="flex align-items-center gap-2">
      <span>{formatExpiry(rowData.valid_until)}</span>
      {isExpired(rowData.valid_until) && <Tag value="Expired" severity="danger" style={{ color: "#fff" }} />}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" appendTo={typeof window !== "undefined" ? document.body : undefined} />
      {initialLoading ? (
        <div className="loading-container">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <Card
            className="p-4"
            title={
              <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-3">
                <div className="text-2xl font-semibold text-900">RFID Cards</div>
                <div className="flex align-items-start gap-2 w-full md:w-auto">
                  <div className="w-full md:w-20rem">
                    {header}
                  </div>
                  <Button label="Add" icon="pi pi-plus" onClick={onCreate} />
                </div>
              </div>
            }
          >
            <DataTable
              value={rfidCards}
              paginator
              rows={10}
              filters={filters}
              dataKey="uid"
              globalFilterFields={["uid", "card_code", "emaid"]}
              emptyMessage="No RFID cards found."
              loading={refreshing}
              stripedRows
              showGridlines={false}
              tableStyle={{ minWidth: '50rem', border: '1px solid #e5e7eb' }}
            >
              <Column field="uid" header="RFID Uid" sortable />
              <Column field="card_code" header="Card Code" sortable />
              <Column field="emaid" header="Owner Emaid" sortable />
              <Column field="status" header="Status" body={statusBody} sortable />
              <Column field="valid_until" header="Valid Until" body={validUntilBody} sortable />
              <Column
                header="Actions"
                style={{ width: "120px" }}
                body={(row: RfidCard) => (
                  <RfidActionsCell row={row} onAssign={onAssign} onUnassign={onUnassign} onDeactivate={onDeactivate} onDelete={onDelete} onUpdate={onUpdate} />
                )}
              />
            </DataTable>
          </Card>

          {renderModal()}
        </>
      )}
    </>
  );
};

export default RfidManagement;